/**
 * Utility functions for privileges management
 */

/**
 * Groups privileges by category and resource from their Resource.title
 * @param {Array} allPrivileges - Array of privilege objects
 * @returns {Object} Grouped privileges by category and resource
 */
export const groupPrivilegesBySections = (allPrivileges) => {
  const groupedByCategory = {};

  allPrivileges.forEach((priv) => {
    const resourceTitle = priv.Resource.title;
    const titleParts = resourceTitle.split(' / ');
    const category = titleParts[0] || 'Other';
    const resource = titleParts[1] || resourceTitle;

    if (!groupedByCategory[category]) {
      groupedByCategory[category] = {};
    }

    if (!groupedByCategory[category][resource]) {
      groupedByCategory[category][resource] = [];
    }

    groupedByCategory[category][resource].push(priv);
  });

  const sortedCategories = Object.keys(groupedByCategory).sort();
  const result = {};

  sortedCategories.forEach((category) => {
    const sortedResources = Object.keys(groupedByCategory[category]).sort();
    result[category] = {};
    sortedResources.forEach((resource) => {
      result[category][resource] = groupedByCategory[category][resource];
    });
  });

  return result;
};

/**
 * Computes section selection state from sections and selected privilege IDs
 * @param {Object} sections - Grouped sections object
 * @param {Array} selectedIds - Array of selected privilege IDs
 * @returns {Object} Section selection state
 */
export const computeSectionSelectionState = (sections, selectedIds) => {
  const sectionState = {};
  Object.entries(sections).forEach(([category, resources]) => {
    Object.entries(resources).forEach(([resource, privs]) => {
      const resourceKey = `${category}-${resource}`;
      const allSelected = privs.every((priv) => selectedIds.includes(priv.id));
      sectionState[resourceKey] = allSelected;
    });
  });
  return sectionState;
};

/**
 * Creates boxes state array from privileges and selected IDs
 * @param {Array} privileges - All privileges
 * @param {Array} selectedIds - Selected privilege IDs
 * @returns {Array} Boolean array of selection states
 */
export const createBoxesState = (privileges, selectedIds) => {
  return privileges.map((priv) => selectedIds.includes(priv.id));
};

/**
 * Extracts selected privilege IDs from boxes state
 * @param {Array} privileges - All privileges
 * @param {Array} boxesState - Boolean array of selection states
 * @returns {Array} Selected privilege IDs
 */
export const extractSelectedIds = (privileges, boxesState) => {
  return privileges.filter((_, idx) => boxesState[idx]).map((p) => p.id);
};

/**
 * Parses category and resource from a privilege's Resource.title
 * @param {Object} priv - Privilege object
 * @returns {Object} { category, resource }
 */
export const parsePrivilegeResource = (priv) => {
  const titleParts = priv.Resource.title.split(' / ');
  return {
    category: titleParts[0] || 'Other',
    resource: titleParts[1] || priv.Resource.title,
  };
};

/**
 * Computes privilege changes between old and new role
 * @param {Array} oldRolePrivileges - Old role privilege IDs
 * @param {Array} newRolePrivileges - New role privilege IDs
 * @returns {Object} { added, removed }
 */
export const computePrivilegeChanges = (
  oldRolePrivileges,
  newRolePrivileges
) => {
  return {
    added: newRolePrivileges.filter((id) => !oldRolePrivileges.includes(id)),
    removed: oldRolePrivileges.filter((id) => !newRolePrivileges.includes(id)),
  };
};

/**
 * Extracts Privilege objects from a raw API response (e.g. /api/users/me allPrivileges).
 * Each item has { Privilege: { id, name, Resource: { title } } }.
 * @param {Array} rawItems - Raw privilege response items
 * @returns {Array} Flat privilege objects with Resource attached
 */
export const extractPrivilegesFromResponse = (rawItems) => {
  return rawItems.map((item) => item.Privilege).filter((p) => p && p.Resource);
};

/**
 * Builds the full data shape that PrivilegesSections needs for a read-only display.
 * Re-uses groupPrivilegesBySections, computeSectionSelectionState, and createBoxesState.
 * @param {Array} rawPrivileges - Raw privilege response items (with { Privilege: { ... } })
 * @returns {Object|null} { privileges, sections, boxesState, selectedPrivileges, isSectionSelected } or null
 */
export const buildReadOnlyPrivilegesData = (rawPrivileges) => {
  const privileges = extractPrivilegesFromResponse(rawPrivileges);
  if (privileges.length === 0) return null;

  const sections = groupPrivilegesBySections(privileges);
  const selectedIds = privileges.map((p) => p.id);
  const boxesState = createBoxesState(privileges, selectedIds);
  const isSectionSelected = computeSectionSelectionState(sections, selectedIds);

  return {
    privileges,
    sections,
    boxesState,
    selectedPrivileges: selectedIds,
    isSectionSelected,
  };
};

/**
 * Finds the "read" privilege in a section's privileges array
 * @param {Array} sectionPrivileges - Privileges for a single resource section
 * @returns {Object|undefined} The read privilege object, or undefined
 */
export const findReadPrivilegeInSection = (sectionPrivileges) => {
  return sectionPrivileges.find((p) => p.name === 'read');
};

/**
 * Checks if any non-read privilege is selected in a section
 * @param {Array} sectionPrivileges - Privileges for a single resource section
 * @param {Array} selectedIds - Currently selected privilege IDs
 * @returns {boolean}
 */
export const hasNonReadPrivilegesSelected = (
  sectionPrivileges,
  selectedIds
) => {
  return sectionPrivileges.some(
    (p) => p.name !== 'read' && selectedIds.includes(p.id)
  );
};

/**
 * Checks if read is "locked" for a section (cannot be unchecked because
 * other non-read privileges are selected).
 * Used by PrivilegesSections to disable the read checkbox.
 * @param {Array} sectionPrivileges - Privileges for a single resource section
 * @param {Map<number|string, number>} privilegeIndexMap - Map from privilege id to global index
 * @param {Array} boxesState - Boolean array of selection states
 * @returns {boolean}
 */
export const isReadLockedInSection = (
  sectionPrivileges,
  privilegeIndexMap,
  boxesState
) => {
  return sectionPrivileges.some((p) => {
    if (p.name === 'read') return false;
    const globalIndex = privilegeIndexMap.get(p.id);
    return globalIndex !== undefined && boxesState[globalIndex];
  });
};

/**
 * Processes a checkbox change with read privilege enforcement:
 * - Prevents unchecking "read" while other privileges in the resource are checked
 * - Auto-checks "read" when any non-read privilege is checked
 * - Auto-unchecks "read" when all non-read privileges are unchecked (if read was auto-checked)
 *
 * @param {Object} params
 * @param {Object} params.priv - The privilege being toggled
 * @param {number} params.index - Index in the global privileges array
 * @param {Array} params.boxesState - Current boxes state
 * @param {Array} params.privileges - All privileges
 * @param {Object} params.sections - Grouped sections
 * @param {Set} params.manuallyCheckedReads - Set of resource keys where read was manually checked
 * @param {Map<number|string, number>} [params.privilegeIndexMap] - Optional pre-built index map for O(1) lookups
 * @returns {Object|null} { newBoxesState, newSelected, updatedManualReads } or null if change is prevented
 */
export const processCheckboxChangeWithReadEnforcement = ({
  priv,
  index,
  boxesState,
  privileges,
  sections,
  manuallyCheckedReads,
  privilegeIndexMap,
}) => {
  const indexMap =
    privilegeIndexMap || new Map(privileges.map((p, i) => [p.id, i]));

  const isChecking = !boxesState[index];
  const { category, resource } = parsePrivilegeResource(priv);
  const sectionPrivileges = sections[category]?.[resource] || [];
  const readPrivilege = findReadPrivilegeInSection(sectionPrivileges);
  const resourceKey = `${category}-${resource}`;
  const updatedManualReads = new Set(manuallyCheckedReads);

  if (priv.name === 'read' && !isChecking) {
    const currentSelected = extractSelectedIds(privileges, boxesState);
    if (hasNonReadPrivilegesSelected(sectionPrivileges, currentSelected)) {
      return null;
    }
    updatedManualReads.delete(resourceKey);
  }

  if (priv.name === 'read' && isChecking) {
    updatedManualReads.add(resourceKey);
  }

  const newBoxesState = [...boxesState];
  newBoxesState[index] = isChecking;
  const newSelected = extractSelectedIds(privileges, newBoxesState);

  if (priv.name !== 'read') {
    if (
      isChecking &&
      readPrivilege &&
      !newSelected.includes(readPrivilege.id)
    ) {
      newSelected.push(readPrivilege.id);
      const readIdx = indexMap.get(readPrivilege.id);
      if (readIdx !== undefined) {
        newBoxesState[readIdx] = true;
      }
    } else if (
      !isChecking &&
      readPrivilege &&
      !hasNonReadPrivilegesSelected(sectionPrivileges, newSelected) &&
      !updatedManualReads.has(resourceKey)
    ) {
      const readIdx = indexMap.get(readPrivilege.id);
      if (readIdx !== undefined) {
        newBoxesState[readIdx] = false;
        const readIdxInSelected = newSelected.indexOf(readPrivilege.id);
        if (readIdxInSelected !== -1) {
          newSelected.splice(readIdxInSelected, 1);
        }
      }
    }
  }

  return { newBoxesState, newSelected, updatedManualReads };
};

/**
 * Initializes manuallyCheckedReads from existing data.
 * Treats all existing read privileges as manually checked on load.
 * @param {Object} sections - Grouped sections
 * @param {Array} selectedIds - Currently selected privilege IDs
 * @returns {Set} Set of resource keys where read is selected
 */
export const initializeManuallyCheckedReads = (sections, selectedIds) => {
  const manualReads = new Set();
  Object.entries(sections).forEach(([category, resources]) => {
    Object.entries(resources).forEach(([resource, sectionPrivileges]) => {
      const readPriv = findReadPrivilegeInSection(sectionPrivileges);
      if (readPriv && selectedIds.includes(readPriv.id)) {
        manualReads.add(`${category}-${resource}`);
      }
    });
  });
  return manualReads;
};

/**
 * Applies privilege changes to current selection
 * @param {Array} currentSelected - Current selected privilege IDs
 * @param {Array} added - IDs to add
 * @param {Array} removed - IDs to remove
 * @returns {Array} Updated privilege IDs
 */
export const applyPrivilegeChanges = (currentSelected, added, removed) => {
  let updated = [...currentSelected];

  added.forEach((id) => {
    if (!updated.includes(id)) {
      updated.push(id);
    }
  });

  removed.forEach((id) => {
    updated = updated.filter((pid) => pid !== id);
  });

  return updated;
};
